import { createTheme, type PaletteMode } from '@mui/material/styles';
import '@mui/x-data-grid/themeAugmentation';
import { alpha } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';

// eQuip Design System Colors (from eQuipDesignSystemExtraction.md)
const colors = {
  primary: {
    main: '#201c51',
    light: '#3b377c',
    dark: '#16123f',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#57bdbb',
    light: '#6bc8c6',
    dark: '#4aa8a6',
    contrastText: '#003132',
  },
  success: {
    main: '#3f7f55',
    light: '#6fa980',
    dark: '#2f5e3f',
    contrastText: '#ffffff',
  },
  warning: {
    main: '#c17d1f',
    light: '#e3a754',
    dark: '#8a5712',
    contrastText: '#ffffff',
  },
  error: {
    main: '#c1504d',
    light: '#d97672',
    dark: '#8e3735',
    contrastText: '#ffffff',
  },
  info: {
    main: '#2f6f94',
    light: '#5d98b9',
    dark: '#1f4d66',
    contrastText: '#ffffff',
  },
  grey: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#eeeeee',
    300: '#e0e0e0',
    400: '#bdbdbd',
    500: '#9e9e9e',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },
};

// Dark mode overrides — lighten the primary so it's visible on dark surfaces
const darkColors = {
  ...colors,
  primary: {
    main: '#6e69c4',
    light: '#9b97d9',
    dark: '#3b377c',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#6bc8c6',
    light: '#8fd9d7',
    dark: '#4aa8a6',
    contrastText: '#003132',
  },
};

// Create a theme for the given palette mode
export function createAppTheme(mode: PaletteMode) {
  const isDark = mode === 'dark';

  const palette = isDark
    ? {
        mode: 'dark' as const,
        ...darkColors,
        background: { default: '#121212', paper: '#1e1e1e' },
        text: {
          primary: 'rgba(255, 255, 255, 0.87)',
          secondary: 'rgba(255, 255, 255, 0.6)',
          disabled: 'rgba(255, 255, 255, 0.38)',
        },
      }
    : {
        mode: 'light' as const,
        ...colors,
        background: { default: '#fafafa', paper: '#ffffff' },
        text: {
          primary: 'rgba(0, 0, 0, 0.87)',
          secondary: 'rgba(0, 0, 0, 0.6)',
          disabled: 'rgba(0, 0, 0, 0.38)',
        },
      };

  const cellBorder = isDark ? '#333333' : '#e0e0e0';
  const headerBg = isDark ? '#2a2a2a' : '#f5f5f5';

  return createTheme({
    palette,
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        fontSize: '2.125rem',
        fontWeight: 300,
        lineHeight: 1.167,
        letterSpacing: '-0.01em',
      },
      h2: {
        fontSize: '1.5rem',
        fontWeight: 500,
        lineHeight: 1.2,
        letterSpacing: '-0.005em',
      },
      h3: {
        fontSize: '1.25rem',
        fontWeight: 600,
        lineHeight: 1.167,
      },
      h4: {
        fontSize: '1.125rem',
        fontWeight: 600,
        lineHeight: 1.235,
      },
      h5: {
        fontSize: '1rem',
        fontWeight: 600,
        lineHeight: 1.334,
      },
      h6: {
        fontSize: '0.875rem',
        fontWeight: 600,
        lineHeight: 1.6,
      },
      body1: {
        fontSize: '1rem',
        fontWeight: 400,
        lineHeight: 1.5,
      },
      body2: {
        fontSize: '0.875rem',
        fontWeight: 400,
        lineHeight: 1.43,
      },
      button: {
        fontSize: '0.875rem',
        fontWeight: 500,
        lineHeight: 1.75,
        textTransform: 'uppercase',
      },
    },
    spacing: 8, // 8px base spacing unit
    shape: {
      borderRadius: 8,
    },
    components: {
      // Customize MUI components to match eQuip design
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            textTransform: 'none', // Override default uppercase
            fontWeight: 500,
            transition: 'all 0.2s ease-in-out',
          },
          contained: {
            boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0,0,0,0.12), 0 2px 4px rgba(0,0,0,0.08)',
              transform: 'translateY(-1px)',
            },
            '&:active': {
              transform: 'translateY(0)',
            },
          },
          outlined: {
            borderWidth: '1.5px',
            '&:hover': {
              borderWidth: '1.5px',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            boxShadow: isDark
              ? '0 1px 3px rgba(0,0,0,0.3), 0 4px 12px rgba(0,0,0,0.25)'
              : '0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.08)',
            borderRadius: 12,
            transition: 'box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out',
          },
        },
      },
      MuiPaper: {
        variants: [
          {
            props: { variant: 'metricStat' },
            style: ({ theme }: { theme: Theme }) => ({
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              padding: theme.spacing(0.5, 1),
              borderRadius: theme.shape.borderRadius,
              border: `1px solid ${alpha(theme.palette.secondary.main, 0.4)}`,
              backgroundColor: alpha(theme.palette.secondary.light, 0.15),
              backdropFilter: 'blur(2px)',
            }),
          },
        ],
      },
      MuiAppBar: {
        defaultProps: {
          enableColorOnDark: true,
        },
        styleOverrides: {
          root: {
            boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)',
            backdropFilter: 'blur(8px)',
            backgroundColor: colors.primary.main,
            color: colors.primary.contrastText,
            backgroundImage: 'none',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 8,
              transition: 'box-shadow 0.2s ease-in-out',
              '&.Mui-focused': {
                boxShadow: `0 0 0 3px ${alpha(isDark ? darkColors.primary.main : colors.primary.main, 0.15)}`,
              },
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontWeight: 500,
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          '*': {
            scrollbarWidth: 'thin',
            scrollbarColor: isDark
              ? 'rgba(255,255,255,0.2) transparent'
              : 'rgba(0,0,0,0.15) transparent',
          },
          '*::-webkit-scrollbar': {
            width: 6,
            height: 6,
          },
          '*::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '*::-webkit-scrollbar-thumb': {
            backgroundColor: isDark
              ? 'rgba(255,255,255,0.2)'
              : 'rgba(0,0,0,0.15)',
            borderRadius: 3,
          },
          '*::-webkit-scrollbar-thumb:hover': {
            backgroundColor: isDark
              ? 'rgba(255,255,255,0.35)'
              : 'rgba(0,0,0,0.25)',
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            margin: '1px 4px',
            transition: 'background-color 0.15s ease-in-out',
            '&.Mui-selected': {
              backgroundColor: alpha(isDark ? darkColors.primary.main : colors.primary.main, 0.1),
              borderLeft: `3px solid ${isDark ? darkColors.primary.main : colors.primary.main}`,
              paddingLeft: 'calc(1em - 3px)',
              '&:hover': {
                backgroundColor: alpha(isDark ? darkColors.primary.main : colors.primary.main, 0.16),
              },
            },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            borderRadius: 6,
            fontSize: '0.75rem',
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            ...(isDark && {
              '&.MuiIconButton-colorPrimary:not(.Mui-disabled)': {
                color: darkColors.primary.light,
                backgroundColor: alpha(darkColors.primary.main, 0.15),
                '&:hover': {
                  backgroundColor: alpha(darkColors.primary.main, 0.3),
                },
              },
            }),
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            height: 3,
            borderRadius: '3px 3px 0 0',
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.875rem',
            minHeight: 44,
            transition: 'color 0.15s ease-in-out',
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          standard: {
            borderRadius: 8,
            borderLeft: '4px solid',
            borderLeftColor: 'var(--alert-color)',
          },
          standardSuccess: {
            '--alert-color': isDark ? darkColors.secondary.main : colors.success.main,
          } as Record<string, string>,
          standardError: {
            '--alert-color': colors.error.main,
          } as Record<string, string>,
          standardWarning: {
            '--alert-color': colors.warning.main,
          } as Record<string, string>,
          standardInfo: {
            '--alert-color': isDark ? darkColors.primary.main : colors.info.main,
          } as Record<string, string>,
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              fontWeight: 600,
              fontSize: '0.8125rem',
              letterSpacing: '0.01em',
              backgroundColor: headerBg,
              borderBottom: `2px solid ${cellBorder}`,
              whiteSpace: 'nowrap',
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${cellBorder}`,
            fontSize: '0.875rem',
          },
        },
      },
      MuiSkeleton: {
        defaultProps: {
          animation: 'wave',
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 12,
          },
        },
      },
      MuiDataGrid: {
        styleOverrides: {
          root: {
            border: 'none',
            '& .MuiDataGrid-cell': {
              borderBottom: `1px solid ${cellBorder}`,
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: headerBg,
              borderBottom: `1px solid ${cellBorder}`,
              fontWeight: 600,
              fontSize: '0.8125rem',
              letterSpacing: '0.01em',
            },
            // Fix sort indicator arrow and ⋮ menu appearing to the LEFT of the
            // label in right-aligned numeric columns. MUI DataGrid v7 applies
            // flex-direction: row-reverse to BOTH columnHeaderDraggableContainer
            // AND columnHeaderTitleContainer, plus marginRight:auto on menuIcon.
            // Override all to restore natural left→right order: [Title] [↑] [⋮]
            '& .MuiDataGrid-columnHeader--alignRight .MuiDataGrid-columnHeaderDraggableContainer': {
              flexDirection: 'row !important',
            },
            '& .MuiDataGrid-columnHeader--alignRight .MuiDataGrid-columnHeaderTitleContainer': {
              flexDirection: 'row !important',
            },
            '& .MuiDataGrid-columnHeader--alignRight .MuiDataGrid-columnHeaderTitleContainerContent': {
              display: 'flex',
              justifyContent: 'flex-end',
            },
            '& .MuiDataGrid-columnHeader--alignRight .MuiDataGrid-menuIcon': {
              marginRight: '0 !important',
              marginLeft: '0 !important',
            },
            // IEM tables: highlight rows that require approval
            '& .iem-row-requires-approval': {
              backgroundColor: 'rgba(255, 167, 38, 0.15)', // warning.light approx with transparency
            },
            '& .iem-row-requires-approval.Mui-hover, & .MuiDataGrid-row:hover.iem-row-requires-approval':
              {
                backgroundColor: 'rgba(255, 167, 38, 0.25)',
              },
            '& .Mui-selected.iem-row-requires-approval': {
              backgroundColor: 'rgba(255, 167, 38, 0.30) !important',
            },
          },
        },
      },
    },
  });
}

// Default light theme (kept for backward compatibility)
export const theme = createAppTheme('light');
